import React, { useState } from 'react';
import { ImageService } from '../services/imageService';

interface ImageUploaderProps {
  onImageUploaded?: (imageUrl: string) => void;
  blogPostId?: string;
  containerName?: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  onImageUploaded,
  blogPostId,
  containerName = 'blog-images'
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    setError(null);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload the file
    uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    setError(null);

    try {
      const result = await ImageService.uploadImage(
        file,
        containerName,
        blogPostId
      );

      if (result.success && result.imageUrl) {
        setUploadedImageUrl(result.imageUrl);
        onImageUploaded?.(result.imageUrl);
        console.log('Image uploaded successfully:', result.imageUrl);
      } else {
        setError(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Network error. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      // Create a mock file list for the upload function
      const fileList = {
        0: file,
        length: 1,
        item: (index: number) => index === 0 ? file : null,
        [Symbol.iterator]: function* () { yield file; }
      } as FileList;
      
      const mockEvent = {
        target: { files: fileList }
      } as React.ChangeEvent<HTMLInputElement>;
      
      handleFileSelect(mockEvent);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <div className="image-uploader">
      <div 
        className="upload-area"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        style={{
          border: '2px dashed #ccc',
          borderRadius: '8px',
          padding: '20px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: uploading ? '#f5f5f5' : 'transparent'
        }}
      >
        {preview ? (
          <div>
            <img 
              src={preview} 
              alt="Preview" 
              style={{ maxWidth: '200px', maxHeight: '200px', marginBottom: '10px' }}
            />
            <p>
              {uploading ? 'Uploading...' : 'Upload in progress'}
            </p>
          </div>
        ) : (
          <div>
            <p>Drag and drop an image here, or click to select</p>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploading}
              style={{ marginTop: '10px' }}
            />
          </div>
        )}
      </div>

      {uploading && (
        <div style={{ marginTop: '10px', textAlign: 'center' }}>
          <p>Uploading image...</p>
          <div style={{ 
            width: '100%', 
            height: '4px', 
            backgroundColor: '#e0e0e0',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              backgroundColor: '#007bff',
              animation: 'progress 2s infinite'
            }} />
          </div>
        </div>
      )}

      {error && (
        <div style={{ 
          marginTop: '10px', 
          padding: '10px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24',
          borderRadius: '4px' 
        }}>
          {error}
        </div>
      )}

      {uploadedImageUrl && (
        <div style={{ 
          marginTop: '10px', 
          padding: '10px', 
          backgroundColor: '#d4edda', 
          color: '#155724',
          borderRadius: '4px' 
        }}>
          <p>✅ Image uploaded successfully!</p>
          <p style={{ fontSize: '12px', wordBreak: 'break-all' }}>
            URL: {uploadedImageUrl}
          </p>
        </div>
      )}

      <style>{`
        @keyframes progress {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

export default ImageUploader;